import React, { useEffect, useMemo, useState } from "react";
import { Button, Select, DatePicker, Tag, Tooltip, message } from "antd";
import {
  SettingOutlined,
  EditOutlined,
  LeftOutlined,
  RightOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import dayjsTZ from "../../utils/dayjs";
import { useAuth } from "../../authentication/authHelpers";
import MondayButton from "../commonComponents/MondayButton/MondayButton";
import LoadableComp from "../commonComponents/LoadableComp/LoadableComp";
import GlobalPopupKonfirm from "../commonComponents/GlobalPopupKonfirm";
import { API } from "../../helpers/API/amplifyApi";
import WeekGrid from "./components/WeekGrid";
import WarningsBanner from "./components/WarningsBanner";
import CellActionModal from "./components/CellActionModal";
import FairnessPanel from "./components/FairnessPanel";
import MonthParamsDrawer from "./components/MonthParamsDrawer";
import PolicyModal from "../Konfigurimet/SistemiSettings/RotacioniTurneve/PolicyModal";
import { ymOf, ymToLabel } from "./utils/scheduleHelpers";
import { deriveViewerRole } from "./utils/deriveViewerRole";
import {
  employeesListApiOptions,
  scopeEmployeesList,
} from "../../utils/branchScope";
import "./orari.scss";

const TEAM_NONE = "_unteamed";

function Orari() {
  const { isDarkMode } = useSelector((state) => state.usersList);
  const auth = useAuth();

  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  // null = statics still loading (do not treat as "no policy" yet)
  const [policies, setPolicies] = useState(null);

  const viewer = useMemo(
    () =>
      deriveViewerRole({
        auth,
        policies: policies || [],
        departments,
      }),
    [auth, policies, departments]
  );

  // Defaults differ by viewer role:
  //   - admin/manager → next month (planning view)
  //   - member        → current month (their published schedule)
  const defaultYm = useMemo(() => {
    if (viewer.role === "member") return ymOf(dayjsTZ().startOf("month"));
    return ymOf(dayjsTZ().add(1, "month").startOf("month"));
  }, [viewer.role]);

  const [departmentId, setDepartmentId] = useState(null);
  const [yearMonth, setYearMonth] = useState(
    ymOf(dayjsTZ().add(1, "month").startOf("month"))
  );

  // Members are locked to their own dept + current month. Recompute
  // whenever the viewer-role classification finishes (it depends on
  // async employee + policy fetches).
  useEffect(() => {
    if (viewer.role === "member") {
      setDepartmentId(viewer.employee.employeeDepartmentId);
      setYearMonth(defaultYm);
    } else if (viewer.role === "manager") {
      // Auto-pick their first managed dept if no selection yet.
      if (!viewer.managedDepartmentIds.includes(departmentId)) {
        setDepartmentId(viewer.managedDepartmentIds[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewer.role]);

  const [schedule, setSchedule] = useState(undefined); // undefined = not loaded, null = no schedule, object = loaded
  const [busy, setBusy] = useState(false);
  const [cellTarget, setCellTarget] = useState(null);
  const [teamFilter, setTeamFilter] = useState([]);
  const [paramsOpen, setParamsOpen] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);

  const shiftNameById = useMemo(() => {
    const out = {};
    shifts.forEach((s) => (out[s.shiftId] = s.shiftName));
    return out;
  }, [shifts]);

  const policyForDepartment = useMemo(
    () => (policies || []).find((p) => p.departmentId === departmentId),
    [policies, departmentId]
  );

  const policyDepartmentIds = useMemo(
    () => new Set((policies || []).map((p) => p.departmentId)),
    [policies]
  );

  // Teams (Prishtina/Tirana/etc.) present in the selected department's
  // active roster. Auto-populated and shown as a multi-select; admin
  // can pick one or many to focus the grid + fairness panel.
  const teamOptions = useMemo(() => {
    const counts = new Map();
    for (const e of employees || []) {
      if (e?.employeeDepartmentId !== departmentId) continue;
      if ((e?.employeeStatus || "").toLowerCase() !== "aktiv") continue;
      const t = e?.employeeTeam || TEAM_NONE;
      counts.set(t, (counts.get(t) || 0) + 1);
    }
    return Array.from(counts.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, count]) => ({
        value: name,
        label: `${name === TEAM_NONE ? "Pa ekip" : name} (${count})`,
      }));
  }, [employees, departmentId]);

  // Reset the team filter whenever the department changes — what was
  // selected might no longer apply.
  useEffect(() => {
    setTeamFilter([]);
  }, [departmentId]);

  // Effective excluded employees: per-month override wins, else policy.
  const effectiveExcluded = useMemo(() => {
    if (Array.isArray(schedule?.excludedEmployeeIdsOverride)) {
      return schedule.excludedEmployeeIdsOverride;
    }
    return policyForDepartment?.excludedEmployeeIds || [];
  }, [schedule, policyForDepartment]);

  // Load all four resources independently — a single failure (e.g. the
  // new rotation services not yet deployed in this stage) must not block
  // the page from rendering. `Promise.allSettled` is the right primitive.
  const loadStatics = async () => {
    const [polR, depsR, shsR, empsR] = await Promise.allSettled([
      API.get("shiftRotationPolicies", "/shiftRotationPolicies"),
      API.get("departments", "/departments"),
      API.get("shifts", "/shifts"),
      API.get("employees", "/employees", employeesListApiOptions(auth)),
    ]);

    const pol = polR.status === "fulfilled" ? polR.value || [] : [];
    const deps = depsR.status === "fulfilled" ? depsR.value || [] : [];
    const shs = shsR.status === "fulfilled" ? shsR.value || [] : [];
    const empsRaw = empsR.status === "fulfilled" ? empsR.value || [] : [];
    const emps = scopeEmployeesList(empsRaw, auth);

    setPolicies(pol);
    setDepartments(deps);
    setShifts(shs);
    setEmployees(emps);

    if (polR.status === "rejected") {
      console.warn(
        "[Orari] shiftRotationPolicies API failed — service may not be deployed yet:",
        polR.reason
      );
      message.warning({
        key: "orari-init",
        content:
          "Shërbimi i rotacionit nuk u ngarkua. Krijo politikat e para te Konfigurimet → Sistemi → Rotacioni i turneve, ose verifiko deploy-in.",
        duration: 6,
      });
    }
    if (depsR.status === "rejected" || shsR.status === "rejected") {
      message.error("Nuk u ngarkuan dot departamentet ose turnet.");
    }

    // Auto-select: prefer the first department-with-policy; otherwise
    // fall back to the first department so the picker isn't empty.
    if (!departmentId) {
      const first =
        pol[0]?.departmentId ||
        deps.find((d) => d?.departmentId)?.departmentId ||
        null;
      if (first) setDepartmentId(first);
    }
  };

  const loadSchedule = async () => {
    // Without a department, do not leave `schedule` stuck on `undefined`
    // (full-page spinner). Show the empty state instead.
    if (!departmentId || !yearMonth) {
      setSchedule(null);
      return;
    }
    setSchedule(undefined);
    try {
      const list = await API.get("shiftSchedules", "/shiftSchedules", {
        queryStringParameters: { departmentId, yearMonth },
      });
      setSchedule((list || [])[0] || null);
    } catch (e) {
      console.error(e);
      message.error("Nuk u ngarkua dot kalendari.");
      setSchedule(null);
    }
  };

  useEffect(() => {
    loadStatics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadSchedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentId, yearMonth]);

  const onGenerate = async () => {
    if (!departmentId || !yearMonth) return;
    try {
      setBusy(true);
      const result = await API.post(
        "shiftSchedules",
        "/shiftSchedules/generate",
        { body: { departmentId, yearMonth } }
      );
      if (result?.ok === false) {
        message.error(`Nuk u gjenerua: ${result.error}`);
      } else {
        message.success("Kalendari u gjenerua.");
        setSchedule(result.schedule);
      }
    } catch (e) {
      console.error(e);
      message.error("Gjenerimi dështoi.");
    } finally {
      setBusy(false);
    }
  };

  const onPublish = async () => {
    if (!schedule?.scheduleId) return;
    try {
      setBusy(true);
      const result = await API.post(
        "shiftSchedules",
        `/shiftSchedules/${encodeURIComponent(schedule.scheduleId)}/publish`
      );
      if (result?.ok === false) {
        message.error(`Nuk u publikua: ${result.error}`);
      } else {
        message.success(`U publikua. Njoftime: ${result.notified || 0}`);
        setSchedule(result.schedule);
      }
    } catch (e) {
      console.error(e);
      message.error("Publikimi dështoi.");
    } finally {
      setBusy(false);
    }
  };

  const onCellClick = (weekStart, employeeId, _assignment) => {
    if (!schedule) return;
    setCellTarget({ weekStart, employeeId });
  };

  const hasSchedule = !!schedule;
  const isPublished = schedule?.status === "published";

  const canManage = viewer.role === "admin" || viewer.role === "manager";

  // For non-admins, only show dropdown options they're allowed to pick.
  const departmentOptions = useMemo(() => {
    if (viewer.role === "admin") {
      return departments.map((d) => ({
        value: d.departmentId,
        label: d.departmentName,
        hasPolicy: policyDepartmentIds.has(d.departmentId),
      }));
    }
    if (viewer.role === "manager") {
      return departments
        .filter((d) => viewer.managedDepartmentIds.includes(d.departmentId))
        .map((d) => ({
          value: d.departmentId,
          label: d.departmentName,
          hasPolicy: policyDepartmentIds.has(d.departmentId),
        }));
    }
    // member: locked to own dept, but we still show the (single) option
    // so the picker isn't blank.
    if (viewer.role === "member") {
      const own = departments.find(
        (d) => d.departmentId === viewer.employee.employeeDepartmentId
      );
      return own
        ? [
            {
              value: own.departmentId,
              label: own.departmentName,
              hasPolicy: true,
            },
          ]
        : [];
    }
    return [];
  }, [viewer, departments, policyDepartmentIds]);

  // Wait for policies before deciding the user is a non-participant.
  if (policies === null) {
    return <LoadableComp loading />;
  }

  // Non-participants should not land here at all (sidebar also hides /orari).
  if (viewer.role === "none") {
    return <Navigate to="/kryefaqja" replace />;
  }

  return (
    <LoadableComp loading={false}>
      <div className={`orari-page ${isDarkMode ? "dark" : ""}`}>
        <div className="orari-page__toolbar">
          <Select
            placeholder="Departamenti"
            value={departmentId}
            onChange={setDepartmentId}
            style={{ minWidth: 260 }}
            showSearch
            optionFilterProp="label"
            options={departmentOptions}
            disabled={viewer.role === "member"}
            optionRender={(opt) => (
              <span style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <span>{opt.data.label}</span>
                {opt.data.hasPolicy ? null : (
                  <Tag color="default" style={{ margin: 0 }}>
                    pa politikë
                  </Tag>
                )}
              </span>
            )}
          />

          <div className="orari-month-nav">
            <Tooltip title="Muaji paraardhës">
              <Button
                icon={<LeftOutlined />}
                onClick={() =>
                  setYearMonth(
                    ymOf(dayjsTZ(`${yearMonth}-01`).subtract(1, "month"))
                  )
                }
                disabled={viewer.role === "member"}
                aria-label="Muaji paraardhës"
              />
            </Tooltip>
            <DatePicker
              picker="month"
              format="MM/YYYY"
              value={dayjsTZ(`${yearMonth}-01`)}
              onChange={(d) =>
                setYearMonth(ymOf(d ? dayjsTZ(d) : dayjsTZ()))
              }
              allowClear={false}
              disabled={viewer.role === "member"}
            />
            <Tooltip title="Muaji pasardhës">
              <Button
                icon={<RightOutlined />}
                onClick={() =>
                  setYearMonth(
                    ymOf(dayjsTZ(`${yearMonth}-01`).add(1, "month"))
                  )
                }
                disabled={viewer.role === "member"}
                aria-label="Muaji pasardhës"
              />
            </Tooltip>
            <Tooltip title="Shko te muaji aktual">
              <Button
                icon={<CalendarOutlined />}
                onClick={() => setYearMonth(ymOf(dayjsTZ()))}
                disabled={
                  viewer.role === "member" || yearMonth === ymOf(dayjsTZ())
                }
              >
                Sot
              </Button>
            </Tooltip>
          </div>

          {teamOptions.length > 1 ? (
            <Select
              mode="multiple"
              allowClear
              placeholder="Të gjitha ekipet"
              value={teamFilter}
              onChange={setTeamFilter}
              options={teamOptions}
              style={{ minWidth: 240 }}
              maxTagCount="responsive"
              disabled={viewer.role === "member"}
            />
          ) : null}

          <div style={{ flex: 1 }} />

          <Tag color={isPublished ? "green" : hasSchedule ? "blue" : "default"}>
            {isPublished
              ? `Publikuar • ${ymToLabel(yearMonth)}`
              : hasSchedule
                ? `Draft • ${ymToLabel(yearMonth)}`
                : `Pa kalendar • ${ymToLabel(yearMonth)}`}
          </Tag>
          {schedule?.fairnessMode ? (
            schedule.fairnessMode === "everyone_once" ? (
              <Tag
                color="purple"
                title="Modaliteti: Çdo punonjës saktësisht 1 javë pasdite në muaj. Kuotat min. seniorë/juniorë NUK aplikohen."
              >
                Çdo punonjës 1 javë (kuotat s'aplikohen)
              </Tag>
            ) : (
              <Tag
                color="geekblue"
                title="Modaliteti: Sipas kuotave. Çdo (rol, ekip) ka min. seniorë + juniorë në javë sipas politikës."
              >
                Sipas kuotave
              </Tag>
            )
          ) : null}

          {canManage ? (
            <>
              <MondayButton
                Icon={<EditOutlined />}
                className="mondayButtonGrey"
                disabled={!departmentId}
                onClick={() => setPolicyOpen(true)}
              >
                {policyForDepartment ? "Përditëso politikën" : "Krijo politikë"}
              </MondayButton>
              <MondayButton
                Icon={<SettingOutlined />}
                className="mondayButtonGrey"
                disabled={!hasSchedule}
                onClick={() => setParamsOpen(true)}
              >
                Parametrat e muajit
              </MondayButton>
              <GlobalPopupKonfirm
                Button={
                  <MondayButton
                    className="mondayButtonBlue"
                    disabled={busy || !policyForDepartment}
                  >
                    {hasSchedule ? "Rigjeneo" : "Gjenero"}
                  </MondayButton>
                }
                title={
                  hasSchedule
                    ? "Rigjenerimi mban kyçur cellat manuale. Vazhdo?"
                    : "Të gjenerohet kalendari për këtë muaj?"
                }
                onConfirm={onGenerate}
                okText="Po"
                cancelText="Jo"
              />

              <GlobalPopupKonfirm
                Button={
                  <MondayButton
                    className="mondayButtonGreen"
                    disabled={busy || !hasSchedule}
                  >
                    {isPublished ? "Republiko" : "Publiko"}
                  </MondayButton>
                }
                title={
                  isPublished
                    ? "Të dërgohen njoftime për ndryshimet që nga publikimi i fundit?"
                    : "Të publikohet kalendari? Punonjësit do të marrin njoftim."
                }
                onConfirm={onPublish}
                okText="Po"
                cancelText="Jo"
              />
            </>
          ) : (
            <Tag color="blue">Vetëm leximi</Tag>
          )}
        </div>

        {canManage && !policyForDepartment ? (
          <div className="orari-page__warnings">
            Ky departament nuk ka politikë rotacioni. Kliko{" "}
            <b>Krijo politikë</b> në shiritin lart për ta krijuar tani.
          </div>
        ) : null}

        {canManage ? <WarningsBanner warnings={schedule?.warnings} /> : null}

        {canManage && schedule ? (
          <FairnessPanel schedule={schedule} employees={employees} />
        ) : null}

        {schedule === undefined ? (
          <div className="orari-grid__wrap orari-grid__wrap--loading">
            <LoadableComp loading={true} />
            <div className="orari-loading-hint">Duke ngarkuar kalendarin…</div>
          </div>
        ) : schedule === null ? (
          <div className="orari-grid__wrap" style={{ padding: 16 }}>
            {!departmentId
              ? "Zgjidh një departament për të parë orarin."
              : viewer.role === "member"
              ? `Nuk ka kalendar të publikuar për ${ymToLabel(yearMonth)}.`
              : (
                <>
                  Nuk ka kalendar të ruajtur për {ymToLabel(yearMonth)}. Kliko
                  <strong> Gjenero</strong> për të krijuar një draft.
                </>
              )}
          </div>
        ) : (
          <WeekGrid
            schedule={schedule}
            employees={employees}
            shiftNameById={shiftNameById}
            onCellClick={canManage ? onCellClick : undefined}
            teamFilter={teamFilter}
            excludedEmployeeIds={effectiveExcluded}
          />
        )}

        {canManage && cellTarget && schedule ? (
          <CellActionModal
            open={!!cellTarget}
            schedule={schedule}
            weekStart={cellTarget.weekStart}
            employeeId={cellTarget.employeeId}
            employees={employees}
            shifts={shifts}
            onClose={() => setCellTarget(null)}
            onUpdated={(updated) => setSchedule(updated)}
          />
        ) : null}

        {canManage && schedule ? (
          <MonthParamsDrawer
            open={paramsOpen}
            onClose={() => setParamsOpen(false)}
            schedule={schedule}
            policy={policyForDepartment}
            employees={employees}
            isDarkMode={isDarkMode}
            onSaved={(s) => setSchedule(s)}
            onSavedAndRegenerated={(s) => setSchedule(s)}
            onEditPolicy={() => {
              setParamsOpen(false);
              setPolicyOpen(true);
            }}
          />
        ) : null}

        {canManage && policyOpen ? (
          <PolicyModal
            open={policyOpen}
            initial={policyForDepartment || null}
            departments={
              policyForDepartment
                ? departments
                : // when creating, lock the picker to the currently selected dept
                  departments.filter((d) => d.departmentId === departmentId)
            }
            shifts={shifts}
            employees={employees}
            onClose={() => setPolicyOpen(false)}
            onSaved={async () => {
              await loadStatics();
              await loadSchedule();
            }}
          />
        ) : null}
      </div>
    </LoadableComp>
  );
}

export default Orari;
